import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BUSINESS } from "@/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent. Thank you for contacting Dirt Cheapest Sod Corp.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error("Your message could not be sent. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="py-16 sm:py-24 bg-secondary/40">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <h2
                    data-testid="contact-heading"
                    className="font-heading font-semibold tracking-tight text-primary text-2xl sm:text-3xl lg:text-4xl"
                >
                    Contact Us
                </h2>
                <div className="mt-10 grid lg:grid-cols-2 gap-12">
                    <div className="space-y-8">
                        <div className="flex items-start gap-4">
                            <MapPin className="h-5 w-5 mt-1 text-primary shrink-0" aria-hidden="true" />
                            <div>
                                <h3 className="font-heading font-semibold text-lg">Address</h3>
                                <p data-testid="contact-address" className="mt-1 text-base text-muted-foreground">
                                    {BUSINESS.addressLines[0]}
                                    <br />
                                    {BUSINESS.addressLines[1]}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Phone className="h-5 w-5 mt-1 text-primary shrink-0" aria-hidden="true" />
                            <div>
                                <h3 className="font-heading font-semibold text-lg">Phone</h3>
                                <p data-testid="contact-phone" className="mt-1 text-base text-muted-foreground">
                                    <a href={BUSINESS.phoneHref} className="hover:text-primary transition-colors">
                                        {BUSINESS.phone}
                                    </a>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Mail className="h-5 w-5 mt-1 text-primary shrink-0" aria-hidden="true" />
                            <div>
                                <h3 className="font-heading font-semibold text-lg">Email</h3>
                                <p data-testid="contact-email" className="mt-1 text-base text-muted-foreground">
                                    <a href={BUSINESS.emailHref} className="hover:text-primary transition-colors">
                                        {BUSINESS.email}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                    <form
                        data-testid="contact-form"
                        onSubmit={handleSubmit}
                        className="rounded-lg border border-border bg-card p-6 sm:p-8 shadow-sm space-y-5"
                    >
                        <div className="space-y-2">
                            <Label htmlFor="contact-name">Name</Label>
                            <Input
                                id="contact-name"
                                data-testid="contact-name-input"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                required
                                maxLength={200}
                                autoComplete="name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contact-email">Email</Label>
                            <Input
                                id="contact-email"
                                data-testid="contact-email-input"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                required
                                maxLength={320}
                                autoComplete="email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contact-message">Message</Label>
                            <Textarea
                                id="contact-message"
                                data-testid="contact-message-input"
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                required
                                maxLength={5000}
                                rows={5}
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="contact-submit-button"
                            disabled={submitting}
                            className="w-full sm:w-auto rounded-full px-8"
                        >
                            {submitting ? "Sending…" : "Send Message"}
                        </Button>
                    </form>
                </div>
            </div>
        </section>
    );
};
